import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export type ThemeMode = 'light' | 'dark';

interface ThemeContextValue {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  toggle: () => void;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

const STORAGE_KEY = 'ct_theme';
const prefersDarkQuery = '(prefers-color-scheme: dark)';

const isBrowser = typeof window !== 'undefined';

const getSystemTheme = (): ThemeMode => {
  if (!isBrowser || typeof window.matchMedia !== 'function') {
    return 'light';
  }

  return window.matchMedia(prefersDarkQuery).matches ? 'dark' : 'light';
};

const readStoredTheme = (): ThemeMode | null => {
  if (!isBrowser) {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch (error) {
    // localStorage may be unavailable; fall back to system preference
  }

  return null;
};

const ThemeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const storedThemeRef = useRef<ThemeMode | null>(readStoredTheme());
  const [hasStoredPreference, setHasStoredPreference] = useState(
    () => storedThemeRef.current !== null,
  );
  const [theme, setThemeState] = useState<ThemeMode>(
    () => storedThemeRef.current ?? getSystemTheme(),
  );

  const applyThemeToDocument = useCallback((nextTheme: ThemeMode) => {
    if (!isBrowser) {
      return;
    }

    document.documentElement.setAttribute('data-theme', nextTheme);
    if (document.body) {
      document.body.setAttribute('data-theme', nextTheme);
    }
  }, []);

  useEffect(() => {
    applyThemeToDocument(theme);
  }, [applyThemeToDocument, theme]);

  const persistTheme = useCallback((nextTheme: ThemeMode) => {
    if (!isBrowser) {
      return;
    }

    try {
      window.localStorage.setItem(STORAGE_KEY, nextTheme);
    } catch (error) {
      // ignore persistence errors
    }
  }, []);

  const setTheme = useCallback(
    (mode: ThemeMode) => {
      setThemeState(mode);
      setHasStoredPreference(true);
      persistTheme(mode);
    },
    [persistTheme],
  );

  const toggle = useCallback(() => {
    setThemeState((current) => {
      const nextTheme = current === 'light' ? 'dark' : 'light';
      persistTheme(nextTheme);
      return nextTheme;
    });
    setHasStoredPreference(true);
  }, [persistTheme]);

  useEffect(() => {
    if (!isBrowser || hasStoredPreference || typeof window.matchMedia !== 'function') {
      return;
    }

    const media = window.matchMedia(prefersDarkQuery);
    const handleChange = (event: MediaQueryListEvent) => {
      setThemeState(event.matches ? 'dark' : 'light');
    };

    media.addEventListener('change', handleChange);

    return () => {
      media.removeEventListener('change', handleChange);
    };
  }, [hasStoredPreference]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
      toggle,
    }),
    [setTheme, theme, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }

  return context;
};

export default ThemeProvider;
